<template>
  <div class="flex-column vld-parent" style="min-height: 100px">
    <loading :active="isLoading" :color="'#ff0090'" :loader="'bars'" :is-full-page="false"></loading>
    <div class="h4">Credits Summary</div>
    <div class="d-flex w-100 mb-5 mt-3">
      <ReportFilter
        :businessId="businessId"
        :showDateRange="false"
        :showLocations="true"
        :showPlanGroups="false"
        :showPlans="true"
        @applyFilter="applyFilter"
        :pre-selected-business-plans="preSelectedBusinessPlans"
      ></ReportFilter>
    </div>
    <div class="card-deck my-3">
      <div v-for="status in statusAggregates" :key="status.status" class="card status-card" @click="applyStatusFilter(status.status)">
        <div class="card-body">
          <div class="flex-column">
            <div class="h3 text-primary text-center">{{ status.planCount }}</div>
            <div class="text-muted text-center small">{{ status.status }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-column w-100">
      <div class="d-flex justify-content-between mt-4 mb-2">
        <h6 class="my-auto">Detailed History</h6>
        <div class="d-flex my-auto">
          <ExportButton
            class="my-auto"
            :filename="`PredictileCredits.csv`"
            :requestPayload="requestPayload"
            :url="`/businesses/${businessId}/reports/credits/export`"
            @onError="didErrorExport"
            label="csv"
            type="flat"
          ></ExportButton>
          <button type="button" class="btn btn-link my-auto pr-0" @click="loadData"><fa icon="sync" fixed-width /></button>
        </div>
      </div>
      <Table
        :enablePaging="true"
        :columns="columns"
        :page="page"
        :sortColumns="[sortColumn]"
        :sortOrders="[sortOrder]"
        @next="next"
        @previous="previous"
        @viewMember="viewMember"
        @sort="sort"
        @rows="updateRows"
      ></Table>
    </div>
  </div>
</template>

<script>
import ReportFilter from "@/views/business/reports/components/ReportFilter.vue";
import ExportButton from "@/components/ExportButton.vue";
import Table from "@/components/Table.vue";
import moment from "moment";

const columns = [
  {
    type: "hidden",
    property: "userId",
    name: "UserID",
  },
  {
    type: "btn-dynamic",
    property: "fullName",
    name: "Member",
    sortable: true,
    event: "viewMember",
  },
  {
    type: "property",
    property: "businessPlanName",
    name: "Plan Name",
  },
  {
    type: "property",
    property: "planGroup",
    name: "Plan Group",
    sortable: true,
  },
  {
    type: "property",
    property: "businessRedemptionLocationName",
    name: "Location",
    sortable: true,
  },
  {
    type: "property",
    property: "productName",
    name: "Product",
    sortable: true,
  },
  {
    type: "property",
    property: "creditValue",
    name: "Credit Value",
    sortable: true,
  },
  {
    type: "property",
    property: "creditType",
    name: "Credit Type",
  },
  {
    type: "property",
    property: "provisionedDate",
    name: "Provisioned Date",
    sortable: true,
  },
  {
    type: "property",
    property: "redeemedDate",
    name: "Redeemed Date",
    sortable: true,
  },
];

export default {
  name: "BusinessCreditsReport",
  props: {
    businessId: String,
  },
  components: { Table, ReportFilter, ExportButton },
  data() {
    return {
      isLoading: false,
      columns: columns,
      page: {},
      skip: 0,
      take: 20,
      query: null,
      statusAggregates: null,
      selectedStatus: null,

      //filters
      selectedLocations: null,
      selectedBusinessPlans: null,
      preSelectedBusinessPlans: null,

      sortColumn: "provisionedDate",
      sortOrder: "asc",
    };
  },
  computed: {
    todayLocal: {
      get: function() {
        return new moment({ hour: 0, minute: 0, seconds: 0 });
      },
    },
    requestPayload: {
      get: function() {
        return {
          businessLocationIds: this.selectedLocations,
          sortColumn: this.sortColumn,
          sortOrder: this.sortOrder,
          status: this.selectedStatus,
          businessPlanIds: this.selectedBusinessPlans,
        };
      },
    },
  },
  created() {
    if (this.$route.query.businessPlan) {
      this.preSelectedBusinessPlans = this.selectedBusinessPlans = [this.$route.query.businessPlan];
    }
  },
  watch: {
    skip() {
      this.loadData();
    },
    take() {
      this.loadData();
    },
    query() {
      this.loadData();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    updateRows(e) {
      this.take = e;
    },
    didErrorExport(err) {
      console.log(err);
    },
    next() {
      this.skip += this.take;
    },
    previous() {
      this.skip -= this.take;
    },
    loadData() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/reports/credits/search`;
      url += `?skip=${this.skip}&take=${this.take}`;

      this.$axios
        .post(url, this.requestPayload)
        .then((res) => {
          this.page = this.transformPage(res.data.page);
          this.statusAggregates = res.data.statusAggregate;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Report Failed",
            message: "Sorry, It looks like we had some trouble loading this report. You may want to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    transformPage({ request: { skip, take }, total, items }) {
      const formattedData = { skip, take, total, items: [] };
      if (items) {
        for (const item of items) {
          item.formattedPercentage = item.percentage + "%";
          item.provisionedDate = this.displayDate(moment(new Date(item.provisionedDate)));
          item.redeemedDate = item.redeemedDate === null ? "N/A" : this.displayDate(moment(new Date(item.redeemedDate)));
          item.businessRedemptionLocationName = item.businessRedemptionLocationName === null ? "N/A" : item.businessRedemptionLocationName;
          formattedData.items.push(item);
        }
      }

      return formattedData;
    },
    applyStatusFilter(status) {
      this.selectedStatus = status;
      this.loadData();
    },
    displayDate: (date) => date.format("MMM DD, YYYY"),
    formatDate(date) {
      return date.format("YYYY-MM-DD HH:mm");
    },
    applyFilter(filter) {
      this.startDate = filter.startDate;
      this.endDate = filter.endDate;
      this.selectedLocations = filter.selectedLocations;
      this.selectedBusinessPlans = filter.selectedBusinessPlans;
      this.selectedStatus = Array.isArray(filter.selectedStatus) ? null : filter.selectedStatus;
      this.loadData();
    },
    viewMember(item) {
      if (item.userId) {
        this.$router.push(`/businesses/${this.businessId}/members/${item.userId}/plans`);
      }
    },
    sort(sorting) {
      this.sortColumn = sorting.property;
      this.sortOrder = sorting.order;
      this.loadData();
    },
  },
};
</script>
